import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ArrowLeft, Users, Mail, Phone, Briefcase, Building2, Search, Download,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const PMTeamRoster = ({ user }) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAll = async (pid = 'all') => {
    try {
      setLoading(true);
      const url = pid && pid !== 'all'
        ? `${BACKEND_URL}/api/pm/team-roster?project_id=${pid}`
        : `${BACKEND_URL}/api/pm/team-roster`;
      const [r, p] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${BACKEND_URL}/api/projects`, { headers }),
      ]);
      setData(r.data);
      setProjects(p.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load team roster');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(filterProject); /* eslint-disable-next-line */ }, [filterProject]);

  const members = data?.members || [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) =>
      (m.name || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q) ||
      (m.position || '').toLowerCase().includes(q) ||
      (m.department || '').toLowerCase().includes(q) ||
      m.projects.some((pp) => (pp.title || '').toLowerCase().includes(q))
    );
  }, [members, search]);

  const exportCsv = () => {
    if (!filtered.length) { toast.info('Nothing to export'); return; }
    const rows = [
      ['Name', 'Email', 'Phone', 'Position', 'Department', 'Skills', 'Projects', 'Roles'],
      ...filtered.map((m) => [
        m.name, m.email, m.phone, m.position, m.department,
        (m.skills || []).join('; '),
        m.projects.map((p) => p.title).join('; '),
        m.projects.map((p) => p.role_in_project).join('; '),
      ]),
    ];
    const csv = rows.map((r) =>
      r.map((cell) => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `team-roster-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/dashboard" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-1">
              <ArrowLeft className="h-3 w-3 mr-1" /> Back
            </Link>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Users className="h-6 w-6 mr-2 text-blue-600" /> Team Management
            </h1>
            <p className="text-sm text-gray-500">All team members across the projects you manage</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterProject} onValueChange={setFilterProject}>
              <SelectTrigger className="w-[240px]" data-testid="pm-team-project-filter">
                <SelectValue placeholder="All projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-1" /> Export CSV</Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase">Unique members</p>
            <p className="text-2xl font-bold">{data?.summary?.total_members || 0}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase">Total assignments</p>
            <p className="text-2xl font-bold">{data?.summary?.total_assignments || 0}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase">Projects covered</p>
            <p className="text-2xl font-bold">{data?.summary?.projects_covered || 0}</p>
          </CardContent></Card>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input className="pl-9" placeholder="Search by name, email, role, project…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="pm-team-search" />
          </div>
        </div>

        {/* Members */}
        {loading ? (
          <Card><CardContent className="py-10 text-center text-gray-500">Loading roster…</CardContent></Card>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-gray-500">
            <Users className="h-10 w-10 mx-auto mb-2 text-gray-300" />
            <p>No team members match your filters.</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((m) => (
              <Card key={m.identifier} data-testid={`team-member-${m.identifier}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>{m.name}</span>
                    {m.projects.length > 1 && (
                      <Badge variant="outline" className="text-xs">{m.projects.length} projects</Badge>
                    )}
                  </CardTitle>
                  {m.position && (
                    <p className="text-xs text-gray-500 inline-flex items-center"><Briefcase className="h-3 w-3 mr-1" />{m.position}</p>
                  )}
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  <div className="space-y-1">
                    {m.email && (
                      <p className="inline-flex items-center text-gray-700">
                        <Mail className="h-3 w-3 mr-1 text-gray-500" />
                        <a href={`mailto:${m.email}`} className="text-blue-600 hover:underline">{m.email}</a>
                      </p>
                    )}
                    {m.phone && (
                      <p className="inline-flex items-center text-gray-700">
                        <Phone className="h-3 w-3 mr-1 text-gray-500" />
                        <a href={`tel:${m.phone}`} className="hover:underline">{m.phone}</a>
                      </p>
                    )}
                    {m.department && (
                      <p className="inline-flex items-center text-gray-600">
                        <Building2 className="h-3 w-3 mr-1 text-gray-500" />{m.department}
                      </p>
                    )}
                  </div>
                  {m.skills && m.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {m.skills.slice(0, 6).map((s) => (
                        <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                      ))}
                      {m.skills.length > 6 && <Badge variant="outline" className="text-xs">+{m.skills.length - 6}</Badge>}
                    </div>
                  )}
                  <div className="pt-2 border-t">
                    <p className="text-xs text-gray-500 mb-1">Assignments</p>
                    <ul className="space-y-1">
                      {m.projects.map((pp) => (
                        <li key={`${m.identifier}-${pp.member_id || pp.id}`} className="flex items-center justify-between text-xs">
                          <Link to={`/projects/${pp.id}`} className="text-blue-600 hover:underline inline-flex items-center">
                            <ExternalLink className="h-3 w-3 mr-1" />{pp.title}
                          </Link>
                          <span className="text-gray-500">{pp.role_in_project}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PMTeamRoster;
